import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, ShoppingCart, Headphones } from 'lucide-react';
import { useCartStore, cartCount } from '../lib/cart-store';
import { useAuthStore } from '../../lib/auth-store';
import { getStoredRef } from '../../lib/ref';
import {
  useCategories,
  useCommerceEnabled,
  usePopularTags,
  useStorefrontContent,
} from '../lib/hooks';
import { isCommercePath } from '@mlm/shared';
import { OmniWordmark } from './storefront-logo';
import { NotificationsBell } from '../../components/notifications-bell';

/** Replicated-link sponsor code carried to /signup so a buyer who lands on the
 * storefront and signs up keeps their sponsor attribution. Read-only — the ref
 * is CAPTURED app-wide by `<RefCapture/>` in App.tsx (which also sees the
 * non-storefront `/r/:refCode` and `/signup?ref=` routes). */
function useStoredRef(): string {
  return getStoredRef() ?? '';
}

/** White utility strip + sticky white search header (logo, search, cart).
 *
 * `leadingSlot` / `actionsSlot` let a host (e.g. the member portal) inject its
 * own controls — a mobile sidebar toggle on the left, and Switch Slot /
 * Notifications / avatar on the right — without forking the header. */
export function Header({
  searchPlaceholder = 'Search wellness kits, coffee, skincare, supplements…',
  brandSub,
  variant = 'full',
  leadingSlot,
  actionsSlot,
  utilLeftSlot,
  stickyTop = false,
  hideAccountBell = false,
}: {
  searchPlaceholder?: string;
  brandSub?: string;
  variant?: 'full' | 'minimal';
  leadingSlot?: ReactNode;
  actionsSlot?: ReactNode;
  /** Overrides the default util-strip links (Feedback / Save More on App / …).
   * The member portal passes a "Shop Now" link to the storefront here. */
  utilLeftSlot?: ReactNode;
  /** Pin the WHOLE top header (util strip + search row) on scroll as one unit,
   * instead of only the search row. Bespoke tenants that want the top bar
   * (util links, Login / Sign Up) to stay put set this. */
  stickyTop?: boolean;
  /** The member portal renders its own NotificationsBell in `actionsSlot`, so it
   * sets this to suppress the util-strip bell and avoid a duplicate. */
  hideAccountBell?: boolean;
}) {
  // Information-site-only tenants have no shop and no membership, so every
  // commerce/auth affordance below is suppressed: Track Order, the account
  // links, Login / Sign Up, search, the category nav and the cart. Read from
  // the tenant config rather than taken as a prop — the header is rendered from
  // a dozen call sites (CMS pages, bespoke shells, the portal) and each one
  // forgetting to thread a flag is exactly how a leak happens.
  const commerce = useCommerceEnabled();
  const count = useCartStore((s) => cartCount(s.lines));
  const ref = useStoredRef();
  const authed = !!useAuthStore((s) => s.token);
  const signupTo = ref ? `/signup?ref=${encodeURIComponent(ref)}` : '/signup';
  // Category nav row is driven by REAL categories (shared by storefront + member);
  // the row hides itself until the admin seeds some.
  const navCats = (useCategories().data ?? []).slice(0, 8);
  // Drop any CMS-stored "Track Order" util link — the smart Track Order button
  // below replaces it, so a tenant config saved before that change doesn't render
  // a duplicate. Match on label too, not just `/track`: older configs stored it
  // as a plain "Track Order" link pointing at `#`, which the href check missed.
  // Also drop CMS links pointing at routes that don't exist in info-only mode
  // (/signup, /sell-on-omni, …) — App doesn't mount them, so they'd dead-end on
  // the catch-all redirect. Filtered at render: live tenants already store their
  // own copies of these links, so rewriting the data would clobber admin edits.
  const utilLinks = useStorefrontContent()
    .data.header.utilLinks.filter(
      (l) => l.href !== '/track' && l.label.trim().toLowerCase() !== 'track order',
    )
    .filter((l) => commerce || !isCommercePath(l.href));

  // The search box is self-contained: it owns its term (seeded from / synced to
  // the `?q=` param so it reflects the active search) and submits to the shop
  // results page. This makes the header search work on EVERY storefront page
  // without each page wiring it in.
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [term, setTerm] = useState(params.get('q') ?? '');
  useEffect(() => {
    setTerm(params.get('q') ?? '');
  }, [params]);
  const runSearch = (t: string) => {
    // Search gets its own results page — NOT the merchant "Shop" page (which is a
    // specific merchant / Platform Branch storefront). Empty submit → home.
    navigate(t.trim() ? `/search?q=${encodeURIComponent(t.trim())}` : '/');
  };
  const submitSearch = () => runSearch(term);

  // Admin-curated popular searches (falls back to sales-derived), surfaced as a
  // focus dropdown so they're visible from the header — not just the /search page.
  const popular = usePopularTags().data ?? [];
  const [searchFocused, setSearchFocused] = useState(false);
  const showSuggestions = searchFocused && popular.length > 0;
  const pickSuggestion = (s: string) => {
    setTerm(s);
    setSearchFocused(false);
    runSearch(s);
  };

  if (variant === 'minimal') {
    return (
      <header className="border-b border-[#ececec] bg-white">
        <div className="mx-auto flex max-w-[1180px] items-center gap-4 px-4 py-4">
          <Link to="/" className="flex items-center gap-3">
            <OmniWordmark size={32} sub={brandSub} />
          </Link>
          <span className="ml-auto flex items-center gap-2 text-[13px] text-[#767676]">
            <Headphones className="h-4 w-4" /> Secure checkout
          </span>
        </div>
      </header>
    );
  }

  return (
    // `stickyTop` pins the util strip + search row together (as one unit) on
    // scroll; otherwise only the <header> below sticks and the util strip scrolls
    // away. `contents` keeps the non-sticky DOM identical to before.
    <div className={stickyTop ? 'sticky top-0 z-40' : 'contents'}>
      <div className="border-b border-[#ececec] bg-white text-[12.5px] font-medium text-[#666]">
        <div className="mx-auto flex min-h-[34px] max-w-[1180px] items-center gap-[18px] px-4">
          {/* Smart Track Order: logged-in members go straight to their Orders
              tab; visitors are sent to login and returned to Orders after. Lives
              at the very top of the header so it's reachable from every page. */}
          {commerce && (
            <button
              type="button"
              onClick={() => navigate(authed ? '/orders' : '/login?next=/orders')}
              className="cursor-pointer font-semibold hover:text-o"
            >
              Track Order
            </button>
          )}
          {utilLeftSlot ?? (
            // Non-essential util links would overflow the fixed-height strip on
            // phones — hide them below sm; the account links (ml-auto) stay.
            <div className="hidden items-center gap-[18px] sm:flex">
              {utilLinks.map((link, idx) => (
                <span key={link.label} className="flex items-center gap-[18px]">
                  {idx > 0 && <i className="h-3 w-px bg-[#ececec]" />}
                  {link.href.startsWith('/') ? (
                    <Link to={link.href} className="cursor-pointer hover:text-o">
                      {link.label}
                    </Link>
                  ) : (
                    <a href={link.href} className="cursor-pointer hover:text-o">
                      {link.label}
                    </a>
                  )}
                </span>
              ))}
            </div>
          )}
          <div className="ml-auto flex items-center gap-3">
            {/* The bell only renders here on the public storefront (where the
                portal Layout isn't mounted), so a logged-in shopper sees their
                notifications beside "My Account". The portal sets
                `hideAccountBell` since it supplies its own bell. */}
            {commerce && authed && !hideAccountBell && <NotificationsBell compact />}
            {!commerce ? null : authed ? (
              <Link to="/dashboard" className="font-bold text-o">
                My Account
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="rounded-full border border-o px-3.5 py-[5px] font-semibold text-o transition-colors hover:bg-o hover:text-white"
                >
                  Login
                </Link>
                <Link
                  to={signupTo}
                  className="rounded-full bg-o px-3.5 py-[5px] font-semibold text-white transition-colors hover:bg-o-d"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      <header className={`border-b border-[#ececec] bg-white${stickyTop ? '' : ' sticky top-0 z-40'}`}>
        <div className="mx-auto flex max-w-[1180px] flex-wrap items-center gap-3 px-4 py-4 sm:flex-nowrap sm:gap-[34px]">
          {leadingSlot}
          <Link to="/" className="flex flex-none items-center">
            <OmniWordmark size={38} sub={brandSub} />
          </Link>
          {/* On phones the action cluster (Switch Slot / bells / avatar, or just
              the cart) would crowd the search to nothing, so the search drops to
              its own full-width row below sm and goes inline (flex-1) from sm up.
              Both search and the category nav shop the catalog, so the whole
              column disappears on an information-only site — leaving the logo
              row to collapse naturally around whatever slots the host passed. */}
          {commerce && (
          <div className="order-last min-w-0 grow basis-full sm:order-none sm:basis-0">
            <div className="relative">
              <form
                role="search"
                onSubmit={(e) => {
                  e.preventDefault();
                  submitSearch();
                }}
                className="flex overflow-hidden rounded-[3px] border-2 border-o"
              >
                <input
                  placeholder={searchPlaceholder}
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  // Delay so a click on a suggestion registers before blur hides it.
                  onBlur={() => setTimeout(() => setSearchFocused(false), 120)}
                  className="flex-1 px-3.5 py-[11px] text-sm outline-none"
                />
                <button
                  type="submit"
                  aria-label="Search"
                  className="flex w-16 items-center justify-center bg-o text-white transition-colors hover:bg-o-d"
                >
                  <Search className="h-5 w-5" />
                </button>
              </form>
              {showSuggestions && (
                <div className="absolute inset-x-0 top-full z-50 mt-1 rounded-[4px] border border-[#ececec] bg-white p-2 shadow-lg">
                  <div className="px-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-[#bbb]">
                    Popular searches
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {popular.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pickSuggestion(s)}
                        className="rounded-full border border-[#eee] bg-[#fafafa] px-2.5 py-1 text-[12.5px] text-[#555] hover:border-o hover:text-o"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {navCats.length > 0 && (
              <div className="mt-[7px] hidden flex-wrap gap-3.5 text-xs font-medium text-[#999] sm:flex">
                {navCats.map((c) => (
                  <Link key={c.id} to={`/shop?category=${c.id}`} className="cursor-pointer hover:text-o">
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
          )}
          {commerce && (
          <Link to="/cart" className="relative ml-auto flex-none text-[#444] sm:ml-0" aria-label="Cart">
            <ShoppingCart className="h-7 w-7" />
            {count > 0 && (
              <span className="absolute -right-2.5 -top-1.5 rounded-full bg-o px-1.5 text-[11px] font-extrabold leading-[18px] text-white">
                {count}
              </span>
            )}
          </Link>
          )}
          {actionsSlot}
        </div>
      </header>
    </div>
  );
}
